import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type { CategoryRow } from '../ogm-hub';
import * as Hub from '../ogm-hub';

export type SupplierCategory = {
	parentId: number;
	supplierId: number;
} & CategoryRow;

export const getSupplierCategoryId = (supplierCategory: SupplierCategory) => {
	return [
		supplierCategory.supplierId,
		supplierCategory.parentId,
		supplierCategory.id,
	].join('|');
};

export type ISupplierCategoriesState = EntityState<SupplierCategory>;

export const supplierCategoryAdapter = createEntityAdapter<SupplierCategory>({
	selectId: getSupplierCategoryId,
});

export const GetTopLevelCategoriesForSupplier = createAsyncThunk<
	SupplierCategory[],
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/GetTopLevelCategoriesForSupplier',
	async (supplierId: number, { getState, dispatch, rejectWithValue }) => {
		const supplierCategories = selectTopLevelCategoriesForSupplier(
			getState(),
			supplierId
		);

		if (supplierCategories.length == 0) {
			const response = await dispatch(
				fetchTopLevelCategoriesForSupplier(supplierId)
			);
			if (fetchTopLevelCategoriesForSupplier.fulfilled.match(response)) {
				return response.payload;
			} else {
				rejectWithValue(response.payload);
			}
		}

		return supplierCategories;
	}
);

const fetchTopLevelCategoriesForSupplier = createAsyncThunk<
	SupplierCategory[],
	number,
	{ rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/fetchTopLevelCategoriesForSupplier',
	async (supplierId: number, { rejectWithValue }) => {
		try {
			const supplierCategories: Hub.GetTopLevelCategoriesResult =
				await unwrapFetch2(
					Hub.SupplierCategoriesController.GetTopLevelCategoriesForSupplier(
						supplierId
					)
				);

			if (supplierCategories.errors && supplierCategories.errors.length > 0) {
				return rejectWithValue(supplierCategories.errors);
			}

			return supplierCategories.categories.map((cat) => ({
				...cat,
				parentId: null,
				supplierId: supplierId,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const GetSubCategoriesOfCategory = createAsyncThunk<
	SupplierCategory[],
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/GetSubCategoriesOfCategory',
	async (categoryId: number, { dispatch, rejectWithValue }) => {
		const response = await dispatch(fetchSubCategoriesForCategory(categoryId));
		if (fetchSubCategoriesForCategory.fulfilled.match(response)) {
			return response.payload;
		} else {
			rejectWithValue(response.payload);
		}
	}
);

const fetchSubCategoriesForCategory = createAsyncThunk<
	SupplierCategory[],
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/fetchSubCategoriesForCategory',
	async (categoryId: number, { getState, rejectWithValue }) => {
		try {
			const supplierCategories: Hub.GetSubLevelCategoriesResult =
				await unwrapFetch2(
					Hub.SupplierCategoriesController.GetSubCategories(categoryId)
				);

			if (supplierCategories.errors && supplierCategories.errors.length > 0) {
				return rejectWithValue(supplierCategories.errors);
			}

			const category = getCategoryByEntityId(getState(), categoryId);
			return supplierCategories.categories.map((cat) => ({
				...cat,
				parentId: categoryId,
				supplierId: category?.supplierId,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const GetCategoriesByIds = createAsyncThunk<
	SupplierCategory[],
	{ supplierId: number; categoryIds: number[] },
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/GetCategoriesByIds',
	async (args, { getState, dispatch }) => {
		const supplierCategories = selectCategoriesByEntityIds(
			getState(),
			args.categoryIds
		);
		const missingCategoryIds = [];

		const parentIds = supplierCategories.map((x) => x.parentId);
		parentIds.forEach((parentId) => {
			if (missingCategoryIds.findIndex((x) => x.id === parentId) < 0) {
				missingCategoryIds.push(parentId);
			}
		});

		args.categoryIds.forEach((categoryId) => {
			if (supplierCategories.findIndex((x) => x.id === categoryId) < 0) {
				missingCategoryIds.push(categoryId);
			}
		});

		if (missingCategoryIds.length > 0) {
			const missingCategoriesResponse = await dispatch(
				fetchCategoriesAndParentsByIds({
					categoryIds: missingCategoryIds,
					supplierId: args.supplierId,
				})
			);
			if (
				fetchCategoriesAndParentsByIds.fulfilled.match(
					missingCategoriesResponse
				)
			) {
				supplierCategories.push(...missingCategoriesResponse.payload);
			}
		}

		return supplierCategories;
	}
);

const fetchCategoriesAndParentsByIds = createAsyncThunk<
	SupplierCategory[],
	{ categoryIds: number[]; supplierId: number },
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'supplierCategoriesSlice/fetchCategoriesAndParentsByIds',
	async (args, { rejectWithValue }) => {
		try {
			const supplierCategories: Hub.GetCategoryAndParentsResult =
				await unwrapFetch2(
					Hub.SupplierCategoriesController.GetCategoriesAndParentsByCategoryIds(
						{
							categoryIds: args.categoryIds,
						}
					)
				);

			if (supplierCategories.errors && supplierCategories.errors.length > 0) {
				return rejectWithValue(supplierCategories.errors);
			}

			return supplierCategories.categories.map((cat) => ({
				...cat,
				supplierId: args.supplierId,
			}));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const SupplierCategoriesSlice = createSlice({
	name: 'supplierCategoriesSlice',
	initialState: supplierCategoryAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(
			fetchTopLevelCategoriesForSupplier.fulfilled,
			(state, action) => {
				supplierCategoryAdapter.upsertMany(state, action.payload);
			}
		);
		builder.addCase(
			fetchCategoriesAndParentsByIds.fulfilled,
			(state, action) => {
				supplierCategoryAdapter.upsertMany(state, action.payload);
			}
		);
		builder.addCase(
			fetchSubCategoriesForCategory.fulfilled,
			(state, action) => {
				supplierCategoryAdapter.upsertMany(state, action.payload);
			}
		);
	},
});

export function selectCategoriesByEntityIds(
	state: IAppState,
	categoryIds: number[]
): SupplierCategory[] {
	const ids = state.supplierCategories.ids.filter((x) =>
		categoryIds.includes(parseInt((x as string).split('|')[2], 10))
	);
	return ids.map((id) => state.supplierCategories.entities[id]);
}

export function getCategoryByEntityId(
	state: IAppState,
	categoryId: number
): SupplierCategory {
	const ids = state.supplierCategories.ids.filter(
		(x) => (x as string).split('|')[2] == categoryId.toString()
	);
	if (ids.length > 0) {
		return state.supplierCategories.entities[ids[0]];
	}
	return null;
}

export function selectSubCategoriesOfCategory(
	state: IAppState,
	categoryId: number
): SupplierCategory[] {
	const ids = state.supplierCategories.ids.filter(
		(x) => (x as string).split('|')[1] == categoryId.toString()
	);
	return ids.map((id) => state.supplierCategories.entities[id]);
}

export function selectTopLevelCategoriesForSupplier(
	state: IAppState,
	supplierId: number
): SupplierCategory[] {
	const ids = state.supplierCategories.ids.filter((x) => {
		const id = (x as string).split('|');
		return id[0] == supplierId.toString() && id[1] == '';
	});
	return ids.map((id) => state.supplierCategories.entities[id]);
}
